import svgPaths from "../imports/svg-au6zg4ohi6";
import { motion, type Transition } from "framer-motion";
import type { ReactNode } from "react";
import BurgerArrow from "./BurgerArrow";

interface DeLaHuertaBurgerProps {
  isExploded: boolean;
}

const enterTransition: Transition = {
  duration: 0.3,
  ease: [0.4, 0, 0.2, 1],
};

const leaveTransition: Transition = {
  type: "spring",
  stiffness: 550,
  damping: 18,
  mass: 1,
};

function getTransition(isExploded: boolean): Transition {
  return isExploded ? enterTransition : leaveTransition;
}

function LabelText({
  lines,
  className = "",
  lineClassNames = [],
}: {
  lines: string[];
  className?: string;
  lineClassNames?: string[];
}) {
  return (
    <div
      className={[
        "flex flex-col justify-center whitespace-nowrap",
        "font-['Diary_Notes',sans-serif] text-red-500",
        "text-[8px] leading-[8px] tracking-[0.16px]",
        "min-[380px]:text-[9px] min-[380px]:leading-[9px] min-[380px]:tracking-[0.18px]",
        "sm:text-[10px] sm:leading-[10px] sm:tracking-[0.2px]",
        "lg:text-[12px] lg:leading-[12px] lg:tracking-[0.24px]",
        className,
      ].join(" ")}
    >
      {lines.map((line, index) => (
        <p
          key={`${line}-${index}`}
          className={["mb-0", lineClassNames[index] ?? ""].join(" ")}
        >
          {line}
        </p>
      ))}
    </div>
  );
}

function FlowLabel({
  isExploded,
  side,
  inset = 0,
  children,
}: {
  isExploded: boolean;
  side: "left" | "right";
  inset?: number;
  children?: ReactNode;
}) {
  const explodedX = side === "left" ? inset : -inset;
  const hiddenX = side === "left" ? explodedX - 8 : explodedX + 8;

  return (
    <motion.div
      initial={false}
      className={[
        "pointer-events-none flex shrink-0 overflow-visible",
        side === "left" ? "justify-end" : "justify-start",
      ].join(" ")}
      animate={{
        opacity: isExploded && children ? 1 : 0,
        x: isExploded && children ? explodedX : hiddenX,
      }}
      transition={getTransition(isExploded)}
      aria-hidden={!isExploded}
    >
      <div className="flex items-center gap-2 whitespace-nowrap">
        {children}
      </div>
    </motion.div>
  );
}

function SideLabel({
  isExploded,
  side,
  lines,
  arrowDirection,
  arrowColor,
  textClassName = "",
  lineClassNames = [],
  inset = 24,
}: {
  isExploded: boolean;
  side: "left" | "right";
  lines: string[];
  arrowDirection: "left" | "right" | "left-bottom" | "right-top" | "right-bottom";
  arrowColor?: string;
  textClassName?: string;
  lineClassNames?: string[];
  inset?: number;
}) {
  return (
    <FlowLabel isExploded={isExploded} side={side} inset={inset}>
      {side === "left" ? (
        <>
          <LabelText
            lines={lines}
            className={textClassName}
            lineClassNames={lineClassNames}
          />
          <BurgerArrow direction={arrowDirection} color={arrowColor} />
        </>
      ) : (
        <>
          <BurgerArrow direction={arrowDirection} color={arrowColor} />
          <LabelText
            lines={lines}
            className={textClassName}
            lineClassNames={lineClassNames}
          />
        </>
      )}
    </FlowLabel>
  );
}
function DualRightLabels({ isExploded }: { isExploded: boolean }) {
  return (
    <FlowLabel isExploded={isExploded} side="right" inset={24}>
      <div className="flex flex-col items-start gap-2">
        <div className="flex items-center gap-2">
          <BurgerArrow direction="right-top" color="var(--red-500)" />
          <LabelText lines={["TYBO", "x2"]} />
        </div>

        <div className="flex items-center gap-2">
          <BurgerArrow direction="right-bottom" color="var(--red-500)" />
          <LabelText lines={["MEDALLÓN", "VEGGIE"]} />
        </div>
      </div>
    </FlowLabel>
  );
}

function LayerRow({
  isExploded,
  y,
  left,
  right,
  children,
}: {
  isExploded: boolean;
  y: number;
  left?: ReactNode;
  right?: ReactNode;
  children: ReactNode;
}) {
  return (
    <motion.div
      className="
        mx-auto grid w-fit items-center justify-center gap-x-1
        grid-cols-[68px_133px_68px]
        sm:grid-cols-[80px_133px_80px]
        md:grid-cols-[94px_133px_94px]
      "
      animate={{ y: isExploded ? y : 0 }}
      transition={getTransition(isExploded)}
    >
      <div className="flex h-0 items-center justify-end overflow-visible">
        {left ?? null}
      </div>

      <div className="flex items-center justify-center">
        {children}
      </div>

      <div className="flex h-0 items-center justify-start overflow-visible">
        {right ?? null}
      </div>
    </motion.div>
  );
}

function TopBun() {
  return (
    <div className="w-[108px] py-[2px] sm:w-[120px] md:w-[133px]">
      <div className="h-[36px] rounded-tl-[80px] rounded-tr-[80px] rounded-bl-[8px] rounded-br-[8px] border-4 border-red-500 sm:h-[40px] md:h-[43.75px]" />
    </div>
  );
}

function CebollaCaramelizada() {
  return (
    <div className="w-[108px] py-[2px] sm:w-[120px] md:w-[133px]">
      <div className="flex w-full items-center justify-center px-[8px] sm:px-[9px] md:px-[10px]">
        <div className="flex w-full items-center justify-center gap-[4px] sm:gap-[4.5px] md:gap-[5px]">
          {[...Array(4)].map((_, i) => (
            <div key={`top-${i}`} className="h-[5.203px] w-[16px] sm:w-[18px] md:w-[19.611px]">
              <svg
                className="block h-full w-full overflow-visible"
                fill="none"
                preserveAspectRatio="none"
                viewBox="0 0 23.6105 9.20279"
              >
                <path
                  d={svgPaths.p1af93200}
                  stroke="var(--red-500)"
                  strokeLinecap="round"
                  strokeWidth="4"
                />
              </svg>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-[2px] flex h-[5.203px] w-full items-center justify-center gap-[4px] sm:gap-[4.5px] md:gap-[5px]">
        {[...Array(5)].map((_, i) => (
          <div
            key={`bottom-${i}`}
            className="-scale-y-100 h-[5.203px] w-[16px] sm:w-[18px] md:w-[19.611px]"
          >
            <svg
              className="block h-full w-full overflow-visible"
              fill="none"
              preserveAspectRatio="none"
              viewBox="0 0 23.6105 9.20279"
            >
              <path
                d={svgPaths.p1af93200}
                stroke="var(--red-500)"
                strokeLinecap="round"
                strokeWidth="4"
              />
            </svg>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChampinonesSalteados() {
  return (
    <div className="w-[108px] p-[2px] sm:w-[120px] md:w-[133px]">
      <div className="flex w-full items-center justify-between">
        {[...Array(7)].map((_, i) => (
          <div key={i} className="h-[8px] w-[10px] sm:w-[11px] md:w-[12px]">
            <svg
              className="block h-full w-full overflow-visible"
              fill="none"
              preserveAspectRatio="none"
              viewBox="0 0 15 11"
            >
              <path
                d={svgPaths.p387a1c00}
                stroke="var(--red-500)"
                strokeLinecap="round"
                strokeWidth="3"
              />
              <path d={svgPaths.p26f1f880} stroke="var(--red-500)" strokeWidth="3" />
            </svg>
          </div>
        ))}
      </div>
    </div>
  );
}

function Guacamole() {
  return (
    <div className="w-[108px] p-[2px] sm:w-[120px] md:w-[133px]">
      <div className="h-[7px] sm:h-[7.5px] md:h-[7.875px]">
        <svg
          className="block h-full w-full overflow-visible"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 133 11.2399"
        >
          <path d={svgPaths.p38f39180} fill="var(--beige-500)" />
          <path d={svgPaths.p38f39180} stroke="var(--red-500)" strokeWidth="4" />
        </svg>
      </div>
    </div>
  );
}

function MeatCheeseLayer() {
  return (
    <div className="w-[108px] p-[2px] sm:w-[120px] md:w-[133px]">
      <div className="h-[16px] sm:h-[18px] md:h-[19px]">
        <svg
          className="block h-full w-full overflow-visible"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 133 23"
        >
          <path d={svgPaths.p36715d00} fill="var(--red-500)" />
          <path
            d={svgPaths.p13a95540}
            stroke="var(--red-500)"
            strokeLinecap="round"
            strokeWidth="4"
          />
          <path
            d={svgPaths.pca7c900}
            stroke="var(--red-500)"
            strokeLinecap="round"
            strokeWidth="4"
          />
        </svg>
      </div>
    </div>
  );
}

function MayoAhumada() {
  return (
    <div className="-scale-y-100 w-[108px] p-[2px] sm:w-[120px] md:w-[133px]">
      <div className="h-[8px] sm:h-[8.4px] md:h-[8.875px]">
        <svg
          className="block h-full w-full overflow-visible"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 133 11.8701"
        >
          <path d={svgPaths.p326f3cc0} stroke="var(--red-500)" strokeWidth="4" />
        </svg>
      </div>
    </div>
  );
}

function BottomBun() {
  return (
    <div className="w-[108px] py-[2px] sm:w-[120px] md:w-[133px]">
      <div className="h-[16px] rounded-[8px] border-4 border-red-500 sm:h-[17px] md:h-[18.75px]" />
    </div>
  );
}

function VeggieTag() {
  return (
    <div className="relative flex h-[42px] w-[108px] items-center justify-center sm:h-[45px] sm:w-[116px] md:h-[48px] md:w-[122.023px]">
      <svg
        className="absolute left-1/2 top-[3px] h-[34px] w-[108px] -translate-x-1/2 sm:top-[3.4px] sm:h-[37px] sm:w-[116px] md:top-[3.78px] md:h-[40.443px] md:w-[122.023px]"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 122.023 40.443"
      >
        <path d={svgPaths.p224f8300} fill="var(--red-500)" />
      </svg>

      <div className="relative z-10 flex flex-col justify-end whitespace-nowrap text-center font-['Biro_Script_Plus',sans-serif] text-[20px] leading-[16px] tracking-[0.48px] text-red-500 sm:text-[22px] sm:leading-[18px] md:text-[24px] md:leading-[20px]">
        <p>veggie</p>
      </div>
    </div>
  );
}

export default function DeLaHuertaBurger({ isExploded }: DeLaHuertaBurgerProps) {
  const transition = getTransition(isExploded);

  return (
    <div className="mx-auto flex h-full w-full max-w-[420px] flex-col items-center justify-center px-2 sm:px-3">
      <div className="flex w-full flex-col items-center justify-center gap-0">
        <LayerRow
          isExploded={isExploded}
          y={-60}
          right={
            <SideLabel
              isExploded={isExploded}
              side="right"
              lines={["Pan ", "BRIOCHE"]}
              arrowDirection="right"
              arrowColor="var(--red-500)"
            />
          }
        >
          <TopBun />
        </LayerRow>

        <LayerRow
          isExploded={isExploded}
          y={-45}
          left={
            <SideLabel
              isExploded={isExploded}
              side="left"
              lines={["CEBOLLA", "CARAMELIZADA"]}
              arrowDirection="left"
              arrowColor="var(--red-500)"
              textClassName="text-right"
              lineClassNames={[
                "",
                "text-[8px] leading-[8px] sm:text-[10px] sm:leading-[10px] lg:text-[10px] lg:leading-[10px]",
              ]}
            />
          }
        >
          <CebollaCaramelizada />
        </LayerRow>

        <LayerRow
          isExploded={isExploded}
          y={-30}
          left={
            <SideLabel
              isExploded={isExploded}
              side="left"
              lines={["CHAMPIÑONES", "SALTEADOS"]}
              arrowDirection="left"
              arrowColor="var(--red-500)"
              textClassName="text-right"
              lineClassNames={[
                "text-[8px] leading-[8px] sm:text-[10px] sm:leading-[10px] lg:text-[10px] lg:leading-[10px]",
                "",
              ]}
            />
          }
        >
          <ChampinonesSalteados />
        </LayerRow>

        <LayerRow
          isExploded={isExploded}
          y={-15}
          right={
            <SideLabel
              isExploded={isExploded}
              side="right"
              lines={["GUACAMOLE"]}
              arrowDirection="right"
              arrowColor="var(--red-500)"
              textClassName="-translate-y-2"
            />
          }
        >
          <Guacamole />
        </LayerRow>

        <LayerRow
          isExploded={isExploded}
          y={0}
          right={<DualRightLabels isExploded={isExploded} />}
        >
          <MeatCheeseLayer />
        </LayerRow>

        <LayerRow
          isExploded={isExploded}
          y={15}
          left={
            <SideLabel
              isExploded={isExploded}
              side="left"
              lines={["Mayo", "AHUMADA"]}
              arrowDirection="left-bottom"
              arrowColor="var(--red-500)"
              textClassName="text-right"
            />
          }
        >
          <MayoAhumada />
        </LayerRow>

        <motion.div
          className="
            mx-auto grid w-fit items-center justify-center gap-x-1
            grid-cols-[68px_133px_68px]
            sm:grid-cols-[80px_133px_80px]
            md:grid-cols-[94px_133px_94px]
          "
          animate={{ y: isExploded ? 30 : 0 }}
          transition={transition}
        >
          <div />
          <div className="flex justify-center">
            <BottomBun />
          </div>
          <div />
        </motion.div>
      </div>

      <motion.div
        className="mt-4 flex w-full flex-col items-center justify-center"
        animate={{ y: isExploded ? 40 : 0 }}
        transition={transition}
      >
        <p className="whitespace-nowrap font-['Roboto_Mono',monospace] text-[28px] font-bold leading-none tracking-[0.06em] text-red-500 sm:text-[36px] md:text-[44px] lg:text-[48px]">
          DE LA HUERTA
        </p>
        <VeggieTag />
      </motion.div>
    </div>
  );
}

export function DeLaHuertaBurgerClosed() {
  return <DeLaHuertaBurger isExploded={false} />;
}

export function DeLaHuertaBurgerExploded() {
  return <DeLaHuertaBurger isExploded={true} />;
}